/* ============================================================
   <livraison-estimator> — explicateur d'expédition LMMM
   Calcule le jour de départ (lun/mar si pain frais, +jeudi sinon)
   et une fenêtre d'arrivée indicative. Vanilla JS + dayjs (global).
   Chronopost : départ le lundi uniquement, quelle que soit la nature
   du panier (denrées périssables, contrainte transporteur).
   ============================================================ */
(function () {
  'use strict';

  if (window.customElements && customElements.get('livraison-estimator')) return;

  var MOIS_FR = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin',
    'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'];
  var JOURS_FR = ['dimanche', 'lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi'];

  // Jours de départ autorisés (0=dim … 6=sam)
  var DEPARTURE_DAYS_BREAD = [1, 2];        // lundi, mardi
  var DEPARTURE_DAYS_NO_BREAD = [1, 2, 4];  // lundi, mardi, jeudi
  var DEPARTURE_DAYS_CHRONOPOST = [1];      // Chronopost : départ lundi uniquement

  // --- Helpers de date purs (dayjs requis) ---

  // Avance de n jours en sautant samedi (6) et dimanche (0)
  function addBusinessDays(d, n) {
    var date = window.dayjs(d);
    var added = 0;
    while (added < n) {
      date = date.add(1, 'day');
      var dow = date.day();
      if (dow !== 0 && dow !== 6) added++;
    }
    return date;
  }

  // Renvoie le 1er jour dont le jour de semaine ∈ allowedDays, STRICTEMENT après `from`.
  // (On ne part jamais le jour même de disponibilité : il faut au moins le créneau suivant.)
  function nextAllowedDay(from, allowedDays) {
    var date = window.dayjs(from).add(1, 'day');
    for (var i = 0; i < 21; i++) {
      if (allowedDays.indexOf(date.day()) !== -1) return date;
      date = date.add(1, 'day');
    }
    return date;
  }

  function fmtLong(d) {
    // "lundi 2 juin"
    return JOURS_FR[d.day()] + ' ' + d.date() + ' ' + MOIS_FR[d.month()];
  }
  function fmtShort(d) {
    // "2 juin"
    return d.date() + ' ' + MOIS_FR[d.month()];
  }

  function camelCase(str) {
    return str.replace(/-([a-z])/g, function (_, c) { return c.toUpperCase(); });
  }

  class LivraisonEstimator extends HTMLElement {
    connectedCallback() {
      // dayjs absent (rare) → on laisse le fallback <noscript>/markup statique
      if (!window.dayjs) return;

      this.dateInput = this.querySelector('[data-lmm="order-date"]');
      this.carrierSelect = this.querySelector('[data-lmm="carrier"]');
      this.cpInput = this.querySelector('[data-lmm="cp"]');
      this.result = this.querySelector('[data-lmm="result"]');

      if (!this.dateInput || !this.carrierSelect || !this.result) return;

      // Sets de codes postaux à surcoût (depuis les réglages éditables)
      this.cpIles = new Set((this.dataset.cpIles || '').split(/\s+/).filter(Boolean));
      this.cpZones = new Set((this.dataset.cpZones || '').split(/\s+/).filter(Boolean));

      var today = window.dayjs();
      this.dateInput.value = today.format('YYYY-MM-DD');
      this.dateInput.min = today.format('YYYY-MM-DD');

      var self = this;
      var controls = this.querySelectorAll('[data-lmm="order-date"], [name^="lmm-has-bread"], [data-lmm="carrier"], [data-lmm="cp"]');
      controls.forEach(function (el) {
        el.addEventListener('change', function () { self.compute(); });
        el.addEventListener('input', function () { self.compute(); });
      });

      this.compute();
    }

    readInt(name, fallback) {
      var v = parseInt(this.dataset[name], 10);
      return isNaN(v) ? fallback : v;
    }

    compute() {
      var dateVal = this.dateInput.value;
      var breadEl = this.querySelector('[name^="lmm-has-bread"]:checked');
      var hasBread = breadEl ? breadEl.value === 'yes' : false;
      var carrier = this.carrierSelect.value;

      var processingDays = this.readInt('processingDays', 0);
      var transitMin = this.readInt(camelCase(carrier) + 'Min', 1);
      var transitMax = this.readInt(camelCase(carrier) + 'Max', 3);

      var cp = this.cpInput ? this.cpInput.value.trim() : '';
      var zone = this.classifyZone(cp);

      var orderDate = dateVal ? window.dayjs(dateVal) : window.dayjs();

      // 1. Base de calcul = commande + éventuel délai de préparation (0 j par défaut)
      var base = orderDate.add(processingDays, 'day');

      // 2. Jours de départ autorisés selon panier / transporteur
      var allowed;
      if (carrier === 'chronopost') {
        allowed = DEPARTURE_DAYS_CHRONOPOST;
      } else {
        allowed = hasBread ? DEPARTURE_DAYS_BREAD : DEPARTURE_DAYS_NO_BREAD;
      }

      // 3. Départ : 1er créneau strictement après la base. Si ce créneau tombe dès
      //    le lendemain (marge d'1 j → disponibilité incertaine), on annonce une
      //    fourchette jusqu'au créneau suivant. Chronopost reste un départ ferme.
      var departureMin = nextAllowedDay(base, allowed);
      var gap = departureMin.diff(base, 'day');
      var hasWindow = carrier !== 'chronopost' && gap <= 1;
      var departureMax = hasWindow ? nextAllowedDay(departureMin, allowed) : departureMin;

      // 4. Fenêtre d'arrivée (transit hors week-end) depuis chaque borne de départ
      var arrivalMin = addBusinessDays(departureMin, transitMin);
      var arrivalMax = addBusinessDays(departureMax, transitMax);

      this.render({
        order: orderDate,
        processingDays: processingDays,
        departureMin: departureMin,
        departureMax: departureMax,
        hasWindow: hasWindow,
        arrivalMin: arrivalMin,
        arrivalMax: arrivalMax,
        transitMin: transitMin,
        transitMax: transitMax,
        hasBread: hasBread,
        carrier: carrier,
        zone: zone,
        cp: cp
      });
    }

    classifyZone(cp) {
      if (!/^\d{5}$/.test(cp)) return null;
      if (cp.charAt(0) === '2' && cp.charAt(1) === '0') return 'corse';
      if (this.cpIles.has(cp)) return 'ile';
      if (this.cpZones.has(cp)) return 'zone';
      return 'standard';
    }

    render(r) {
      // Carte résultat — départ (ferme, ou fourchette « si disponibilité »)
      var departureTxt = r.hasWindow
        ? 'entre le ' + fmtShort(r.departureMin) + ' et le ' + fmtLong(r.departureMax)
        : fmtLong(r.departureMin);
      this.setText('[data-lmm="departure"]', departureTxt);

      // Réception : fourchette dès que les bornes diffèrent (par le départ et/ou le transit)
      var windowTxt = r.arrivalMin.isSame(r.arrivalMax, 'day')
        ? 'le ' + fmtLong(r.arrivalMin)
        : 'entre le ' + fmtShort(r.arrivalMin) + ' et le ' + fmtLong(r.arrivalMax);
      this.setText('[data-lmm="arrival"]', windowTxt);

      // Rejoue une micro-animation sur les dates pour signaler le changement
      this.pulseDate('[data-lmm="departure"]');
      this.pulseDate('[data-lmm="arrival"]');

      // Note pédagogique (effet pain / jours de départ)
      var noteEl = this.querySelector('[data-lmm="note"]');
      if (noteEl) {
        if (r.carrier === 'chronopost') {
          noteEl.innerHTML = 'En <strong>Chronopost</strong>, le départ a lieu le <strong>lundi</strong> (livraison express, recommandée pour les denrées périssables).';
        } else if (r.hasBread) {
          noteEl.innerHTML = 'Votre commande contient du <strong>pain frais</strong> : elle ne peut partir que le <strong>lundi ou le mardi</strong> (cuisson fraîche).';
        } else {
          noteEl.innerHTML = 'Sans pain frais, votre commande peut aussi partir le <strong>jeudi</strong>, en plus du lundi et mardi.';
        }
      }

      // Zone de livraison (code postal)
      var zoneEl = this.querySelector('[data-lmm="zone"]');
      if (zoneEl) {
        if (!r.zone) {
          zoneEl.hidden = true;
          zoneEl.className = 'lmm-result__zone';
        } else if (r.zone === 'standard') {
          zoneEl.hidden = false;
          zoneEl.className = 'lmm-result__zone is-standard';
          zoneEl.innerHTML = '<strong>' + r.cp + '</strong> — zone standard : frais de port offerts dès 100 € d\'achat.';
        } else {
          var label = r.zone === 'ile' ? 'île du littoral' : (r.zone === 'corse' ? 'Corse' : 'zone difficile d\'accès');
          zoneEl.hidden = false;
          zoneEl.className = 'lmm-result__zone is-surcout';
          zoneEl.innerHTML = '<strong>' + r.cp + '</strong> — ' + label + ' : un <strong>surcoût transporteur</strong> s\'applique, et la gratuité dès 100 € ne s\'y applique pas.';
        }
      }

      // Timeline : sous-libellés datés
      this.setText('[data-lmm="step-date-1"]', fmtShort(r.order));
      this.setText('[data-lmm="step-date-3"]', JOURS_FR[r.departureMin.day()] + ' ' + r.departureMin.date());
      this.setText('[data-lmm="step-date-4"]', r.transitMin === r.transitMax ? r.transitMin + ' j' : r.transitMin + '–' + r.transitMax + ' j');
      this.setText('[data-lmm="step-date-5"]', fmtShort(r.arrivalMax));

      // États visuels : dernière étape "active", les précédentes "done"
      var steps = this.querySelectorAll('.lmm-timeline__step');
      var lastStep = steps.length - 1;
      steps.forEach(function (step, i) {
        step.classList.toggle('is-done', i < lastStep);
        step.classList.toggle('is-active', i === lastStep);
      });

      this.result.hidden = false;
    }

    setText(sel, txt) {
      var el = this.querySelector(sel);
      if (el) el.textContent = txt;
    }

    pulseDate(sel) {
      var el = this.querySelector(sel);
      if (!el) return;
      el.classList.remove('is-updated');
      void el.offsetWidth; // force un reflow → l'animation rejoue à chaque mise à jour
      el.classList.add('is-updated');
    }
  }

  customElements.define('livraison-estimator', LivraisonEstimator);
})();
